/**
 * XMI (XML Metadata Interchange) Export/Import utilities
 * Supports UML 2.5 XMI format for database modeling
 * Compatible with Enterprise Architect, StarUML, MagicDraw, etc.
 */

/**
 * Export model to XMI 2.5 format
 */
export const exportToXMI = (modelData) => {
  const timestamp = new Date().toISOString();
  const modelId = modelData._id || generateXmiId();
  
  let xmi = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xmi += '<xmi:XMI xmi:version="2.5" xmlns:xmi="http://www.omg.org/spec/XMI/20131001" ';
  xmi += 'xmlns:uml="http://www.omg.org/spec/UML/20131001" ';
  xmi += 'xmlns:dbmodel="http://schema.graphos/dbmodel/1.0">\n';
  
  // Documentation
  xmi += '  <xmi:Documentation exporter="SchemaGraphos" exporterVersion="1.0" exporterID="schema-graphos"/>\n';
  
  // Model element
  xmi += `  <uml:Model xmi:type="uml:Model" xmi:id="${modelId}" name="${escapeXml(modelData.name || 'Untitled Model')}">\n`;
  
  if (modelData.description) {
    xmi += `    <ownedComment xmi:type="uml:Comment" xmi:id="${generateXmiId()}">\n`;
    xmi += `      <body>${escapeXml(modelData.description)}</body>\n`;
    xmi += `    </ownedComment>\n`;
  }
  
  // Model metadata
  xmi += `    <dbmodel:metadata>\n`;
  xmi += `      <dbmodel:type>${escapeXml(modelData.type || 'relational')}</dbmodel:type>\n`;
  xmi += `      <dbmodel:created>${timestamp}</dbmodel:created>\n`;
  if (modelData.globalTags && modelData.globalTags.length > 0) {
    xmi += `      <dbmodel:tags>\n`;
    modelData.globalTags.forEach(tag => {
      const tagText = tag.text || tag.name || 'Tag';
      const tagColor = tag.color || '#3b82f6';
      xmi += `        <dbmodel:tag name="${escapeXml(tagText)}" color="${escapeXml(tagColor)}"/>\n`;
    });
    xmi += `      </dbmodel:tags>\n`;
  }
  if (modelData.businessRules?.trim()) {
    xmi += `      <dbmodel:businessRules><![CDATA[${modelData.businessRules.trim()}]]></dbmodel:businessRules>\n`;
  }
  xmi += `    </dbmodel:metadata>\n`;
  
  const userTypes = modelData.userTypes || [];

  // Export nodes as Classes (Tables/Views/Collections)
  if (modelData.nodes && modelData.nodes.length > 0) {
    modelData.nodes.forEach(node => {
      xmi += exportNode(node, userTypes);
    });
  }
  
  // Export edges as Associations (Relationships)
  if (modelData.edges && modelData.edges.length > 0) {
    modelData.edges.forEach(edge => {
      xmi += exportEdge(edge, modelData.nodes);
    });
  }
  
  xmi += '  </uml:Model>\n';
  xmi += '</xmi:XMI>';
  
  return xmi;
};

/**
 * Export a single node (entity) to XMI
 */
function exportNode(node, userTypes = []) {
  const nodeId = node.id || generateXmiId();
  const nodeType = node.type || 'table';
  let xmi = '';
  
  // Use Class for tables, Component for views, etc.
  const umlType = getUmlType(nodeType);
  
  xmi += `    <packagedElement xmi:type="uml:${umlType}" xmi:id="${nodeId}" `;
  xmi += `name="${escapeXml(node.data?.name || 'Unnamed')}">\n`;
  
  // Description
  if (node.data?.description) {
    xmi += `      <ownedComment xmi:type="uml:Comment" xmi:id="${generateXmiId()}">\n`;
    xmi += `        <body>${escapeXml(node.data.description)}</body>\n`;
    xmi += `      </ownedComment>\n`;
  }
  
  // Business Rules (Entity-level)
  if (node.data?.businessRules?.entity) {
    xmi += `      <ownedComment xmi:type="uml:Comment" xmi:id="${generateXmiId()}" `;
    xmi += `annotatedElement="${nodeId}">\n`;
    xmi += `        <body><![CDATA[--- Business Rules ---\n\n${node.data.businessRules.entity}]]></body>\n`;
    xmi += `      </ownedComment>\n`;
  }
  
  // Stereotypes for entity type
  xmi += `      <dbmodel:stereotype>${nodeType}</dbmodel:stereotype>\n`;
  
  // Position
  if (node.position) {
    xmi += `      <dbmodel:position x="${node.position.x}" y="${node.position.y}"/>\n`;
  }
  
  // Dimensions
  if (node.width || node.measured?.width) {
    xmi += `      <dbmodel:dimensions width="${node.width || node.measured?.width || 250}" `;
    xmi += `height="${node.height || node.measured?.height || 200}"/>\n`;
  }
  
  // Color
  if (node.data?.color) {
    xmi += `      <dbmodel:color>${escapeXml(node.data.color)}</dbmodel:color>\n`;
  }

  // Schema
  if (node.data?.schema) {
    xmi += `      <dbmodel:schema>${escapeXml(node.data.schema)}</dbmodel:schema>\n`;
  }
  
  // Tags
  if (node.data?.tags && node.data.tags.length > 0) {
    xmi += `      <dbmodel:tags>\n`;
    node.data.tags.forEach(tag => {
      const tagText = tag.text || tag.name || 'Tag';
      const tagColor = tag.color || '#3b82f6';
      xmi += `        <dbmodel:tag name="${escapeXml(tagText)}" color="${escapeXml(tagColor)}"/>\n`;
    });
    xmi += `      </dbmodel:tags>\n`;
  }
  
  // SQL Definition for views
  if (nodeType === 'view' && node.data?.sqlDefinition) {
    xmi += `      <dbmodel:sqlDefinition><![CDATA[${node.data.sqlDefinition}]]></dbmodel:sqlDefinition>\n`;
  }
  
  // Materialized view flag
  if (nodeType === 'view' && node.data?.materialized) {
    xmi += `      <dbmodel:materialized>true</dbmodel:materialized>\n`;
  }
  
  // Text content for text nodes
  if (nodeType === 'text' && node.data?.content) {
    xmi += `      <dbmodel:textContent><![CDATA[${node.data.content}]]></dbmodel:textContent>\n`;
  }
  
  // Fields as Properties/Attributes
  if (node.data?.fields && node.data.fields.length > 0) {
    const fieldBusinessRules = node.data?.businessRules?.fields || {};
    node.data.fields.forEach(field => {
      // Add business rules to field if exists
      const fieldWithRules = {
        ...field,
        businessRules: fieldBusinessRules[field.name] || null
      };
      xmi += exportField(fieldWithRules, nodeId, userTypes);
    });
  }
  
  // Indexes
  if (node.data?.indexes && node.data.indexes.length > 0) {
    xmi += `      <dbmodel:indexes>\n`;
    node.data.indexes.forEach(index => {
      xmi += exportIndex(index);
    });
    xmi += `      </dbmodel:indexes>\n`;
  }

  // Partitioning
  if (node.data?.partitioning && node.data.partitioning.type && node.data.partitioning.type !== 'none') {
    xmi += `      <dbmodel:partitioning>\n`;
    xmi += `        <![CDATA[${JSON.stringify(node.data.partitioning)}]]>\n`;
    xmi += `      </dbmodel:partitioning>\n`;
  }
  
  xmi += `    </packagedElement>\n`;
  
  return xmi;
}

/**
 * Export a field (column) to XMI
 */
function exportField(field, ownerId, userTypes = []) {
  const fieldId = field.id || generateXmiId();
  let xmi = '';
  
  xmi += `      <ownedAttribute xmi:type="uml:Property" xmi:id="${fieldId}" `;
  xmi += `name="${escapeXml(field.name)}">\n`;

  // Resolve UDT to base type for the XMI type element; keep the UDT label as stereotype
  let resolvedType = field.type;
  let isUdt = false;
  if (field.type?.startsWith('UDT:')) {
    isUdt = true;
    const udtName = field.type.slice(4);
    const udt = userTypes.find(u => u.name === udtName);
    resolvedType = udt ? udt.baseType : udtName;
  }

  // Type (resolved base type)
  xmi += `        <type xmi:type="uml:PrimitiveType" href="${escapeXml(resolvedType)}"/>\n`;

  // UDT metadata as a tagged stereotype
  if (isUdt) {
    xmi += `        <dbmodel:userDefinedType>${escapeXml(field.type)}</dbmodel:userDefinedType>\n`;
  }
  
  // Description
  if (field.description) {
    xmi += `        <ownedComment xmi:type="uml:Comment" xmi:id="${generateXmiId()}">\n`;
    xmi += `          <body>${escapeXml(field.description)}</body>\n`;
    xmi += `        </ownedComment>\n`;
  }
  
  // Business Rules (Field-level) - passed as parameter
  if (field.businessRules) {
    xmi += `        <ownedComment xmi:type="uml:Comment" xmi:id="${generateXmiId()}" `;
    xmi += `annotatedElement="${fieldId}">\n`;
    xmi += `          <body><![CDATA[--- Business Rules ---\n\n${field.businessRules}]]></body>\n`;
    xmi += `        </ownedComment>\n`;
  }
  
  // Constraints and stereotypes
  xmi += `        <dbmodel:constraints>\n`;
  
  if (field.isPrimaryKey) {
    xmi += `          <dbmodel:primaryKey>true</dbmodel:primaryKey>\n`;
  }
  
  if (field.isForeignKey) {
    xmi += `          <dbmodel:foreignKey>true</dbmodel:foreignKey>\n`;
  }
  
  if (field.nullable !== undefined) {
    xmi += `          <dbmodel:nullable>${field.nullable}</dbmodel:nullable>\n`;
  }
  
  if (field.unique) {
    xmi += `          <dbmodel:unique>true</dbmodel:unique>\n`;
  }
  
  if (field.autoIncrement) {
    xmi += `          <dbmodel:autoIncrement>true</dbmodel:autoIncrement>\n`;
  }
  
  if (field.defaultValue !== undefined) {
    xmi += `          <dbmodel:defaultValue>${escapeXml(String(field.defaultValue))}</dbmodel:defaultValue>\n`;
  }

  if (field.checkConstraint) {
    xmi += `          <dbmodel:checkConstraint>${escapeXml(String(field.checkConstraint))}</dbmodel:checkConstraint>\n`;
  }
  
  xmi += `        </dbmodel:constraints>\n`;
  
  xmi += `      </ownedAttribute>\n`;
  
  return xmi;
}

/**
 * Export an index to XMI
 */
function exportIndex(index) {
  const indexId = index.id || generateXmiId();
  let xmi = '';
  
  xmi += `        <dbmodel:index id="${indexId}" name="${escapeXml(index.name)}" type="${escapeXml(index.type)}">\n`;
  
  if (index.columns && index.columns.length > 0) {
    xmi += `          <dbmodel:columns>\n`;
    index.columns.forEach(column => {
      xmi += `            <dbmodel:column fieldName="${escapeXml(column.fieldName)}" order="${escapeXml(column.order)}"/>\n`;
    });
    xmi += `          </dbmodel:columns>\n`;
  }
  
  xmi += `        </dbmodel:index>\n`;
  
  return xmi;
}

/**
 * Export an edge (relationship) to XMI
 */
function exportEdge(edge, nodes) {
  const edgeId = edge.id || generateXmiId();
  const sourceNode = nodes.find(n => n.id === edge.source);
  const targetNode = nodes.find(n => n.id === edge.target);
  
  if (!sourceNode || !targetNode) {
    return '';
  }
  
  let xmi = '';
  
  xmi += `    <packagedElement xmi:type="uml:Association" xmi:id="${edgeId}" `;
  xmi += `name="${escapeXml(edge.data?.label || '')}">\n`;
  
  // Source end
  xmi += `      <memberEnd xmi:idref="${edge.source}"/>\n`;
  xmi += `      <ownedEnd xmi:type="uml:Property" xmi:id="${generateXmiId()}" `;
  xmi += `type="${edge.source}" association="${edgeId}">\n`;
  
  // Source multiplicity
  const [sourceCard] = (edge.data?.cardinality || '1:N').split(':');
  xmi += `        <lowerValue xmi:type="uml:LiteralInteger" value="${getMultiplicityLower(sourceCard)}"/>\n`;
  xmi += `        <upperValue xmi:type="uml:LiteralUnlimitedNatural" value="${getMultiplicityUpper(sourceCard)}"/>\n`;
  xmi += `      </ownedEnd>\n`;
  
  // Target end
  xmi += `      <memberEnd xmi:idref="${edge.target}"/>\n`;
  xmi += `      <ownedEnd xmi:type="uml:Property" xmi:id="${generateXmiId()}" `;
  xmi += `type="${edge.target}" association="${edgeId}">\n`;
  
  // Target multiplicity
  const [, targetCard] = (edge.data?.cardinality || '1:N').split(':');
  xmi += `        <lowerValue xmi:type="uml:LiteralInteger" value="${getMultiplicityLower(targetCard)}"/>\n`;
  xmi += `        <upperValue xmi:type="uml:LiteralUnlimitedNatural" value="${getMultiplicityUpper(targetCard)}"/>\n`;
  xmi += `      </ownedEnd>\n`;
  
  // Additional metadata
  xmi += `      <dbmodel:relationship>\n`;
  xmi += `        <dbmodel:type>${escapeXml(edge.type || 'default')}</dbmodel:type>\n`;
  if (edge.data?.cardinality) {
    xmi += `        <dbmodel:cardinality>${escapeXml(edge.data.cardinality)}</dbmodel:cardinality>\n`;
  }
  if (edge.data?.cardinalityLocked !== undefined) {
    xmi += `        <dbmodel:cardinalityLocked>${edge.data.cardinalityLocked}</dbmodel:cardinalityLocked>\n`;
  }
  if (edge.data?.sourceField) {
    xmi += `        <dbmodel:sourceField>${escapeXml(edge.data.sourceField)}</dbmodel:sourceField>\n`;
  }
  if (edge.data?.targetField) {
    xmi += `        <dbmodel:targetField>${escapeXml(edge.data.targetField)}</dbmodel:targetField>\n`;
  }
  if (edge.targetHandle) {
    xmi += `        <dbmodel:targetHandle>${escapeXml(edge.targetHandle)}</dbmodel:targetHandle>\n`;
  }
  xmi += `      </dbmodel:relationship>\n`;
  
  xmi += `    </packagedElement>\n`;
  
  return xmi;
}

/**
 * Get UML type for node type
 */
function getUmlType(nodeType) {
  const typeMap = {
    'table': 'Class',
    'view': 'Component',
    'collection': 'Class',
    'fact': 'Class',
    'dimension': 'Class',
    'text': 'Comment'
  };
  return typeMap[nodeType] || 'Class';
}

/**
 * Get multiplicity lower bound
 */
function getMultiplicityLower(cardinality) {
  if (cardinality === '1') return '1';
  if (cardinality === 'N' || cardinality === 'n' || cardinality === 'M' || cardinality === 'm' || cardinality === '*') return '0';
  return '1';
}

/**
 * Get multiplicity upper bound
 */
function getMultiplicityUpper(cardinality) {
  if (cardinality === '1') return '1';
  if (cardinality === 'N' || cardinality === 'n' || cardinality === 'M' || cardinality === 'm' || cardinality === '*') return '*';
  return '1';
}

/**
 * Generate XMI ID
 */
function generateXmiId() {
  return 'xmi-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
}

/**
 * Escape XML special characters
 */
function escapeXml(unsafe) {
  if (unsafe === null || unsafe === undefined) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Download XMI file
 */
export const downloadXMI = (modelData, filename) => {
  const xmiContent = exportToXMI(modelData);
  const blob = new Blob([xmiContent], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xmi') ? filename : `${filename}.xmi`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
